import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminUserDto } from '@rw/shared';
import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Avatar, Button } from '../../components/ui';
import { invalidateCompetitive, useAdminUsers } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatDate, formatKm, timeAgo } from '../../lib/format';
import { toast } from '../../stores/toasts';
import { METHOD_LABEL } from './admin-format';
import { t as tr, useT } from '../../i18n';

type Sort = 'joined' | 'seen' | 'xp';
const SORT_LABEL: Record<Sort, string> = { joined: 'Newest', seen: 'Last seen', xp: 'Top XP' };

/** Every account, for helping a runner who reports a problem. */
export function UsersTab() {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('joined');
  const search = useDeferredValue(q.trim());
  const users = useAdminUsers(search, sort);
  const items = users.data?.pages.flatMap((p) => p.items) ?? [];
  const total = users.data?.pages[0]?.total;
  const [deleting, setDeleting] = useState<AdminUserDto | null>(null);
  const t = useT();

  return (
    <div className="mt-4">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('Search by name or email')}
        className="h-12 w-full rounded-xl border border-ink-700 bg-ink-850 px-4 outline-none focus:border-volt"
        data-testid="admin-user-search"
        aria-label={t('Search accounts')}
      />
      <div className="mt-2 flex items-center gap-1">
        {(Object.keys(SORT_LABEL) as Sort[]).map((k) => (
          <button
            key={k}
            onClick={() => setSort(k)}
            className={`h-8 rounded-lg px-3 text-xs font-bold uppercase ${sort === k ? 'bg-ink-700 text-ink-100' : 'text-ink-400'}`}
          >
            {t(SORT_LABEL[k])}
          </button>
        ))}
        <span className="ml-auto text-xs text-ink-500">{total !== undefined ? t('{n} accounts', { n: total }) : ''}</span>
      </div>
      {users.isPending ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-ink-500">{t('No accounts match.')}</p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="admin-users">
          {items.map((u) => (
            <li key={u.user.id} className="rounded-xl bg-ink-850" data-testid="admin-user">
              <Link to={`/players/${u.user.id}`} className="flex gap-3 p-3 pb-2 text-sm">
                <Avatar name={u.user.displayName} color={u.user.color} src={u.user.avatarUrl} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-semibold">
                      {u.user.displayName}
                      {u.role === 'SUPER_ADMIN' ? <span className="ml-1 text-xs text-volt">{t('owner')}</span> : null}
                    </span>
                    <span className="shrink-0 text-xs text-ink-500">{t('Lv {n}', { n: u.level })}</span>
                  </div>
                  <div className="truncate text-xs text-ink-400">{u.email ?? t('no email (Strava sign-in)')}</div>
                  <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                    <Tag>{METHOD_LABEL[u.signup]}</Tag>
                    {u.stravaConnected ? <Tag strava>{t('Strava connected')}</Tag> : null}
                    {u.club ? <Tag>{u.club.name}</Tag> : null}
                  </div>
                  <div className="mt-1 text-[11px] text-ink-500">
                    {t('Joined {date} · seen {seen} · last run {run}', {
                      date: formatDate(u.createdAt),
                      seen: u.lastSeenAt ? timeAgo(u.lastSeenAt) : '—',
                      run: u.lastRunAt ? timeAgo(u.lastRunAt) : t('never'),
                    })}
                    <br />
                    {t(u.validRuns === 1 ? '{n} run' : '{n} runs', { n: u.validRuns })} · {formatKm(u.totalDistanceM, 0)} km ·{' '}
                    {t(u.territoriesOwned === 1 ? '{n} sector' : '{n} sectors', { n: u.territoriesOwned })}
                  </div>
                </div>
              </Link>
              {u.role === 'SUPER_ADMIN' ? null : (
                <div className="flex justify-end px-3 pb-2">
                  <button
                    onClick={() => setDeleting(u)}
                    className="text-xs font-semibold text-ink-500 hover:text-enemy"
                    data-testid="admin-delete-user"
                  >
                    {t('Delete account')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {users.hasNextPage ? (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void users.fetchNextPage()} disabled={users.isFetchingNextPage}>
          {t(users.isFetchingNextPage ? 'Loading…' : 'Load more')}
        </Button>
      ) : null}
      <DeleteUserSheet user={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}

interface DeletedUserSummary {
  displayName: string;
  runs: number;
  sectorsFreed: number;
  clubsDeleted: number;
  clubsHandedOver: number;
}

/** Deleting is permanent, so the owner types the runner's name to confirm. */
function DeleteUserSheet({ user, onClose }: { user: AdminUserDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState('');
  const t = useT();
  const close = () => {
    setTyped('');
    onClose();
  };
  const del = useMutation({
    mutationFn: (id: string) => api<DeletedUserSummary>(`/admin/users/${id}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      const clubs = r.clubsHandedOver
        ? ` · ${tr('{n} club handed over', { n: r.clubsHandedOver })}`
        : r.clubsDeleted
          ? ` · ${tr('{n} club deleted', { n: r.clubsDeleted })}`
          : '';
      toast({
        kind: 'success',
        title: tr('{name} deleted', { name: r.displayName }),
        body: tr('{runs} runs removed · {sectors} sectors back to neutral', { runs: r.runs, sectors: r.sectorsFreed }) + clubs,
      });
      void qc.invalidateQueries({ queryKey: ['admin'] });
      invalidateCompetitive(qc);
      close();
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Could not delete the account') }),
  });
  const name = user?.user.displayName ?? '';
  // The name stays highlighted inside the translated sentence.
  const [typeBefore, typeAfter = ''] = t('Type {name} to confirm').split('{name}');
  return (
    <Sheet open={!!user} onClose={close} label={t('Delete account')}>
      {user ? (
        <div data-testid="admin-delete-sheet">
          <h2 className="font-display text-2xl font-bold">{t('Delete {name}?', { name })}</h2>
          <p className="mt-2 text-sm text-ink-300">
            {t(
              'This permanently deletes the account ({email}) with all its runs, XP, comments, kudos and notifications. Its {n} sectors go back to neutral, a club it owns passes to its longest member (or is deleted if empty), and Strava access is revoked. This can’t be undone.',
              { email: user.email ?? t('Strava sign-in'), n: user.territoriesOwned },
            )}
          </p>
          <label className="mt-4 block text-sm font-semibold">
            {typeBefore}
            <span className="text-enemy">{name}</span>
            {typeAfter}
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              className="mt-1 h-12 w-full rounded-xl border border-ink-700 bg-ink-850 px-4 outline-none focus:border-enemy"
              data-testid="admin-delete-confirm"
            />
          </label>
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={close}>
              {t('Cancel')}
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              disabled={typed.trim() !== name}
              loading={del.isPending}
              onClick={() => del.mutate(user.user.id)}
              data-testid="admin-delete-submit"
            >
              {t('Delete')}
            </Button>
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}

function Tag({ children, strava }: { children: React.ReactNode; strava?: boolean }) {
  return <span className={`rounded-md px-1.5 py-0.5 ${strava ? 'bg-[#FC5200]/20 text-[#FC8A50]' : 'bg-ink-800 text-ink-300'}`}>{children}</span>;
}
